import { useContext, useMemo, useState } from 'react';

import iziToast from 'izitoast';
import { Box, CheckIcon, Database, Eye, FileSpreadsheet, PlusCircle, Search, X } from 'lucide-react';
import { SubmitErrorHandler, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useParams } from 'react-router-dom';
import { useTranslate } from 'sbx-crm-menu-ui';
import { toast } from 'sonner';
import SheetComponent from 'src/components/shared/sheetComponent/SheetComponent';
import TableComponentV2, { KeyHeader } from 'src/components/shared/TableComponentV2';
import { TextAreaTranslateComponent } from 'src/components/shared/TextAreaTranslateComponent';
import { AI_AGENT_V2_CREATE, AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { AppAgentContext } from '../../../../../context/ContextProvider';
import { deleteAgentSkillServiceV2, upsertAgentSkillServiceV2 } from '../../../../../services/AgentService';
import { deleteFileService, getFolderFilesService } from '../../../../../services/AssetService';
import { appActions, DataStates } from '../../../../../store';
import { AgentSkillClass } from '../../../../../types/AgentSkillClass';
import {
    AgentSkills,
    AiAgentV2Data,
    currentColumnsToReturn,
    SkillSentimentAnalysisConfig,
    SkillType,
} from '../../../../../types/AiAgentV2';
import { EnumSkillRetrieverFields } from '../../../../../types/Common';
import { ModalType } from '../../../../../types/Modal';
import {
    addFilesByFolder,
    extractFolderKeys,
    grantPermission,
    temporallyLabelOpenAiToOracle,
} from '../../../../../utils';
import { FileProps } from '../../../../shared/DragAndDrop';
import RenderFormFieldsComponent, {
    GroupTypes,
} from '../../../../shared/renderFormFieldsComponent/RenderFormFieldsComponent';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../ui/accordion';
import { Button } from '../../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { AgentSetupContext } from '../../AgentSetupComponent';
import AnimatedLoadingSkill from '../AnimatedLoadingScreen';
import AgentSkillListComponent from './AgentSkillListComponent';
import SkillCustomRetrieverFields from './SkillCustomRetrieverFields';
import SkillFileComponent from './SkillFileComponent';
import SkillRequestComponent from './SkillRequestComponent';
import SkillRetrieverComponent from './SkillRetrieverComponent';
import SkillRetrieverComponentDataSourceVersion from './SkillRetrieverComponentDataSourceVersion';
import SkillSummarizeConfig from './SkillSummarizeConfig';

export interface OldValuesSkills {
    description?: string;
    description_en?: string;
    output_format?: string;
    output_format_en?: string;
    prompt?: string;
    prompt_en?: string;
}

export type SkillForm = {
    skills: AgentSkills[];
    new_skill?: AgentSkills;
    skill_index?: number;
};

function getRequiredFields(obj: { [key: string]: any }, requiredFields: string[] = []): string[] {
    for (const key in obj) {
        const value = obj[key];
        if (typeof value === 'object' && 'type' in value && value.type === 'required') {
            // Add the last part of the ref.name (the field key) to the array
            const lastKey = value.ref.name.split('.').pop();
            if (lastKey) {
                requiredFields.push(lastKey);
            }
        } else if (typeof value === 'object') {
            // Recursively search for required fields in nested objects
            getRequiredFields(value as { [key: string]: any }, requiredFields);
        }
    }
    return requiredFields;
}

const classNameTextArea =
    'flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1 bg-white border-gray-300 text-gray-900 !min-h-[50px] !max-h-[200px] transition-all duration-300 ease-in-out';
const classNameLabel = 'form-input-label';
const AgentSkillsComponent = () => {
    const { id } = useParams();
    const { t: common } = useTranslate('common');
    const { t: report } = useTranslate('report');
    const { t: agent } = useTranslate('agent');
    const { t: customMessage } = useTranslate('custom-message');
    const [loading, setLoading] = useState(false);
    const [selectedSource, setSelectedSource] = useState<EnumSkillRetrieverFields>(EnumSkillRetrieverFields.DATABASE);
    const [currentColumnsToReturn, setCurrentColumnsToReturn] = useState<currentColumnsToReturn>({
        columnsToReturn: [],
        group: '',
    });
    const [oldValues, setOldValues] = useState<OldValuesSkills>({});

    const {
        setValue,
        modelOptions,
        skillOptions,
        reportsOptions,
        vectorStorageOptions,
        getValues: getValuesContext,
        getAgent,
        componentCmsList,
    } = useContext(AgentSetupContext);
    const {
        watch,
        control,
        getValues,
        setValue: setValueLocal,
        unregister,
        handleSubmit,
        reset,
        register,
    } = useForm<SkillForm>({
        defaultValues: {
            skills: getValuesContext('new_skills') ?? [],
            new_skill: undefined,
        },
    });

    const {
        state: {
            session: { ai_agent_config, user_id, permissions },
        },
        dispatch: dispatchApp,
    } = useContext(AppAgentContext);

    const [allowCreate, allowEdit] = useMemo(() => {
        return [grantPermission(AI_AGENT_V2_CREATE, permissions), grantPermission(AI_AGENT_V2_EDIT, permissions)];
    }, [permissions]);

    const { fields, append, remove, update } = useFieldArray({
        name: 'skills',
        control,
    });

    const removeSkill = async (skill_id: number, skill: AgentSkills, index: number) => {
        if (id !== 'create') {
            dispatchApp(
                appActions.openModal({
                    type: ModalType.CONFIRMATION,
                    data: {
                        titleClassName: 'text-2xl font-bold text-purple-800 text-center',
                        title: `${common('delete')} ${agent('skill')}?`,
                        description: ``,
                        className:
                            '!bg-gradient-to-br !from-purple-50 !to-pink-50 !rounded-3xl !shadow-2xl w-full max-w-md overflow-hidden',
                        cancelButtonClassName: 'border-pink-500 btn text-pink-600 hover:bg-pink-50 w-[200px]',
                        confirmButtonClassName:
                            'bg-gradient-to-r btn from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white w-[200px]',
                        state: DataStates.AGENT,
                        async onConfirm() {
                            if (id) {
                                dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
                                setLoading(true);
                                const response = await deleteAgentSkillServiceV2(id, skill_id);

                                let items: FileProps[] = [];

                                // const folder = skill.config?.folder

                                const folderKeys = extractFolderKeys(skill);

                                for await (const folderKey of folderKeys) {
                                    const response = await getFolderFilesService(folderKey);
                                    if (response?.success) {
                                        items = items.concat(
                                            response.items.map((item) => ({
                                                key: item.key,
                                                name: item.name,
                                                type: item.mimetype,
                                                size: item.size,
                                            }))
                                        );
                                    }
                                }

                                if (items.length > 0) {
                                    for (const item of items) {
                                        if (item.key) {
                                            await deleteFileService(item.key);
                                        }
                                    }
                                }

                                if (response.success) {
                                    remove(index);
                                    toast.success(`${agent('agent_deleted_skill')}`);
                                } else {
                                    toast.error(`${agent('agent_deleted_skill_error')}`);
                                }

                                setLoading(false);
                                getAgent();
                            }
                        },
                        onClose() {
                            dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
                        },
                    },
                })
            );
        } else {
            if (getValuesContext('skills')) {
                setValue(
                    'new_skills',
                    getValuesContext('skills')!.filter((_, i) => i !== index)
                );
            }

            remove(index);
        }
    };

    const isDesktopOrLaptop = useMediaQuery({
        query: '(min-width: 1280px)',
    });

    const addSkill: SubmitHandler<SkillForm> = async (data) => {
        const skill = { ...data.new_skill };

        if (!skill) return;

        const nSkill = new AgentSkillClass(
            skill,
            ai_agent_config?.folders?.knowledge || '',
            {
                retriever: skill.skill_type === SkillType.SkillStructuredRetriever,
                oldValues,
            },
            selectedSource
        );

        const validation = nSkill.isValidSkill();
        if (validation) {
            toast.error(agent(validation));
            return;
        }

        if (id === 'create') {
            let newSkills = data.skills;
            await nSkill.validateAndTranslateSkill();
            if (typeof data.skill_index === 'number' && data.skill_index !== -1) {
                update(data.skill_index, nSkill.getSkill());
                newSkills = newSkills.map((skill, index) => {
                    if (index === data.skill_index) {
                        return nSkill.getSkill();
                    }
                    return skill;
                });

                reset({
                    skills: newSkills,
                    new_skill: undefined,
                });
                setValue('new_skills', newSkills);
            } else {
                append(skill as AgentSkills);
                await nSkill.validateAndTranslateSkill();
                setValue('new_skills', [...(newSkills as AgentSkills[]), { ...nSkill.getSkill() } as AgentSkills]);

                reset({
                    skills: [...(newSkills as AgentSkills[]), { ...nSkill.getSkill() } as AgentSkills],
                });
            }
        } else {
            if (nSkill && id) {
                setLoading(true);

                await nSkill.validateAndTranslateSkill();

                if (nSkill.skill_id) {
                    nSkill.skill_id = parseInt(nSkill.skill_id.toString());
                }

                const responseUpsert = await upsertAgentSkillServiceV2(
                    nSkill.getSkill(),
                    parseInt(id),
                    !watch('new_skill.skill_id')
                );

                if (!responseUpsert?.success) {
                    toast.error(`${agent('agent_created_skill_error')} ${skill.name}`);
                    reset({
                        new_skill: data.new_skill,
                    });
                } else {
                    if (!nSkill.skill_id && (responseUpsert.item as { skill_id: number })?.skill_id) {
                        nSkill.skill_id = (responseUpsert.item as { skill_id: number }).skill_id;
                    }

                    const response = await nSkill.uploadFolders(parseInt(id), user_id);
                    if (!response?.success) {
                        toast.error(`${agent('agent_created_skill_error')} ${nSkill.getSkill().name}`);
                    }
                    getAgent();
                    toast.success(`${agent('agent_created_skill')} ${skill.name}`);
                }

                setLoading(false);
            }
        }
    };

    const deleteFileFolder = ({ file }: { file: FileProps; name: string }) => {
        dispatchApp(
            appActions.openModal({
                type: ModalType.CONFIRMATION,
                data: {
                    title: common('file'),
                    description: `${customMessage('warn-delete')} ${common('the1')} ${common('file')}?`,
                    state: DataStates.AGENT,
                    async onConfirm() {
                        const res = file?.key ? await deleteFileService(file.key ?? '0') : { success: true };
                        if (res.success) {
                            iziToast.success({
                                message: 'Archivo Eliminado',
                                position: 'topCenter',
                            });
                            // setFilesComponent((prevFiles) => prevFiles.filter(fi => fi.key !== file.key));

                            // if (name === 'config') {
                            //     setFilesComponentConfig((prevFiles) => prevFiles.filter(fi => fi.key !== file.key));
                            // } else {
                            //     setFilesComponentActionRetriever((prevFiles) => prevFiles.filter(fi => fi.key !== file.key));
                            // }

                            const currentFilesComponent = watch(`new_skill.config.filesComponent`);
                            const currentFiles = watch(`new_skill.config.files`);
                            const currentSourceFile = watch(`new_skill.config.source_file`);
                            setValueLocal(
                                `new_skill.config.filesComponent`,
                                currentFilesComponent?.filter(
                                    (itemFile) => itemFile.name.toLowerCase() !== file.name.toLowerCase()
                                )
                            );
                            setValueLocal(
                                `new_skill.config.files`,
                                currentFiles?.filter(
                                    (itemFile) => itemFile.name.toLowerCase() !== file.name.toLowerCase()
                                )
                            );
                            currentSourceFile !== null && setValueLocal(`new_skill.config.source_file`, undefined);
                            dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
                        }
                    },
                    onClose() {
                        dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
                    },
                },
            })
        );
    };

    const unregisterUnusedRetrieverFields = (group: EnumSkillRetrieverFields) => {
        // Desregistrar los campos no utilizados en un solo paso
        const fields = { ...skillRetrieverFields, ...dataAnalysisFields };
        const allOptions = Object.values(fields).flatMap(({ items }) => items.map((item) => item.name));
        const currentOptions = fields[group]?.items.map((item) => item.name) || [];
        const optionsToUnregister = allOptions.filter((name) => !currentOptions.includes(name));

        // Desregistrar opciones globales y específicas
        unregister([
            ...optionsToUnregister,
            ...(group !== EnumSkillRetrieverFields.EXCEL
                ? ['new_skill.config.files', 'new_skill.config.filesComponent', 'new_skill.config.file']
                : []),
        ] as (keyof SkillForm)[]);

        // Resetear columnas actuales y grupo
        setCurrentColumnsToReturn({ columnsToReturn: [], group: '' });
    };

    const skillRetrieverFields = useMemo((): GroupTypes<keyof AiAgentV2Data> => {
        return {
            [EnumSkillRetrieverFields.VECTOR_STORAGE]: {
                columns: 'grid-cols-1 lg:grid-cols-2 mb-3',
                items: [
                    {
                        name: 'new_skill.config.vector_store',
                        label: agent('vector_storage'),
                        placeholder: agent('vector_storage'),
                        type: 'select',
                        options: vectorStorageOptions,
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        rules: { required: true },
                    },
                    {
                        name: 'new_skill.config.vector_store_config.chunk_size',
                        label: common('chunk_size'),
                        placeholder: common('chunk_size'),
                        defaultValue: 4000,
                        noValidateDefaultValues: true,
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        type: 'number',
                    },
                    {
                        name: 'new_skill.config.vector_store_config.chunk_overlap',
                        label: common('chunk_overlap'),
                        defaultValue: 50,
                        noValidateDefaultValues: true,
                        classNameLabel,
                        placeholder: common('chunk_overlap'),
                        disabled: id !== 'create' && !allowEdit,
                        type: 'number',
                    },
                    {
                        name: 'new_skill.config.vector_store_config.lambda_mult',
                        label: agent('lambda_mult'),
                        placeholder: agent('lambda_mult'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        type: 'number',
                    },
                    {
                        name: 'new_skill.config.vector_store_config.search_type',
                        label: agent('search_type'),
                        defaultValue: 'mmr',
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        noValidateDefaultValues: true,
                        placeholder: agent('search_type'),
                    },
                    {
                        name: 'new_skill.config.vector_store_config.fetch_k',
                        label: agent('fetch_k'),
                        classNameLabel,
                        defaultValue: 20,
                        disabled: id !== 'create' && !allowEdit,
                        noValidateDefaultValues: true,
                        placeholder: agent('fetch_k'),
                        type: 'number',
                    },
                    {
                        name: 'new_skill.config.vector_store_config.k',
                        label: 'k',
                        placeholder: 'k',
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        type: 'number',
                        defaultValue: 4,
                        noValidateDefaultValues: true,
                        rules: { required: true },
                    },
                    {
                        name: 'new_skill.config.vector_store_config.score_threshold',
                        label: agent('score_threshold'),
                        placeholder: agent('score_threshold'),
                        disabled: id !== 'create' && !allowEdit,
                        classNameLabel,
                        type: 'number',
                    },
                ],
                label: SkillType.SkillUnstructuredRetriever,
            },
            [EnumSkillRetrieverFields.DATABASE]: {
                columns: 'grid-cols-1 lg:grid-cols-2 mb-3',
                items: [
                    {
                        name: 'new_skill.config.db_host',
                        label: agent('db_host'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        placeholder: agent('db_host'),
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.DATABASE),
                    },
                    {
                        name: 'new_skill.config.db_name',
                        label: agent('db_name'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        placeholder: agent('db_name'),
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.DATABASE),
                    },
                    {
                        name: 'new_skill.config.db_password',
                        label: agent('db_password'),
                        placeholder: agent('db_password'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        type: 'password',
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.DATABASE),
                    },
                    {
                        name: 'new_skill.config.table_name',
                        label: agent('table_name'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        placeholder: agent('table_name'),
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.DATABASE),
                    },
                ],
                label: SkillType.SkillStructuredRetriever,
                description: agent('description_item_retriever_database'),
                icon: Database,
            },
            [EnumSkillRetrieverFields.SBX]: {
                columns: 'grid-cols-1 lg:grid-cols-2 mb-3',
                items: [
                    {
                        name: 'new_skill.config.model_name',
                        label: agent('model_name'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        placeholder: agent('model_name'),
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.SBX),
                    },
                    {
                        name: 'new_skill.config.force',
                        label: agent('force'),
                        classNameLabel,
                        classContainer: 'mt-3 opacity-0',
                        type: 'checkbox',
                        disabled: id !== 'create' && !allowEdit,
                        defaultValue: false,
                        noValidateDefaultValues: true,
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.SBX),
                    },
                ],
                label: SkillType.SkillStructuredRetriever,
                description: agent('description_item_retriever_sbx'),
                icon: Box,
            },
            [EnumSkillRetrieverFields.EXCEL]: {
                columns: 'grid-cols-1 lg:grid-cols-2 mb-3',
                items: [],
                label: SkillType.SkillStructuredRetriever,
                description: agent('description_item_retriever_excel'),
                icon: FileSpreadsheet,
            },
        };
    }, [vectorStorageOptions]);

    const onChangeSkillType = (type: SkillType) => {
        let new_skill: null | AgentSkills;

        if ([SkillType.SkillDataAnalysis, SkillType.SkillClassification, SkillType.SkillSummarize].includes(type)) {
            new_skill = {
                skill_type: type,
                config: {
                    action_retriever_config: null,
                },
            } as AgentSkills;
        } else {
            new_skill = {
                skill_type: type,
            } as AgentSkills;
        }

        if (type === SkillType.SkillDocumentAnalysis) {
            let sentiment_config: SkillSentimentAnalysisConfig = {};

            skillsField[SkillType.SkillSentiment].items.forEach((item) => {
                if (item.noValidateDefaultValues && item.defaultValue && item.name.split('.').at(-1)) {
                    sentiment_config = {
                        ...sentiment_config,
                        [item.name.split('.').at(-1) as string]: item.defaultValue,
                    };
                }
            });

            if (Object.keys(sentiment_config).length > 0) {
                new_skill = {
                    ...new_skill,
                    config: {
                        ...new_skill.config,
                        sentiment_config,
                    },
                };
            }
        }

        reset({
            skills: getValuesContext('new_skills'),
            new_skill,
        });
    };

    const skillsField = useMemo((): GroupTypes<keyof AiAgentV2Data> => {
        return {
            skill: {
                columns: 'grid-cols-1 lg:grid-cols-2  mb-3',
                items: [
                    // {
                    //     name: 'new_skill.skill_type',
                    //     label: common('type'),
                    //     classNameLabel,
                    //     placeholder: `${report('select')} ${common('action')}`,
                    //     options: [
                    //         { value: SkillType.SkillUnstructuredRetriever, label: agent(SkillType.SkillUnstructuredRetriever)},
                    //         { value: SkillType.SkillStructuredRetriever, label: agent(SkillType.SkillStructuredRetriever)},
                    //         // { value: SkillType.SkillRetriever, label: agent(SkillType.SkillRetriever) },
                    //         { value: SkillType.SkillSummarize, label: agent(SkillType.SkillSummarize) },
                    //         { value: SkillType.SkillClassification, label: agent(SkillType.SkillClassification) },
                    //         { value: SkillType.SkillDataAnalysis, label: agent(SkillType.SkillDataAnalysis) },
                    //         { value: SkillType.SkillSentiment, label: agent(SkillType.SkillSentiment) },
                    //         { value: SkillType.SkillRequest, label: agent(SkillType.SkillRequest) },
                    //         {
                    //             value: SkillType.SkillImageAnalysis,
                    //             label: agent(SkillType.SkillImageAnalysis),
                    //         },
                    //         {
                    //             value: SkillType.SkillContentGenerator,
                    //             label: agent(SkillType.SkillContentGenerator),
                    //         },
                    //         {
                    //             value: SkillType.SkillDocumentAnalysis,
                    //             label: agent(SkillType.SkillDocumentAnalysis),
                    //         },
                    //         {
                    //             value: SkillType.SkillSpeechToText,
                    //             label: agent(SkillType.SkillSpeechToText),
                    //         },
                    //     ],
                    //     type: 'select',
                    //     onChange: onChangeSkillType,
                    //     rules: {
                    //         required: true,
                    //     },
                    // },
                    {
                        name: 'new_skill.model',
                        classNameLabel,
                        label: common('model'),
                        placeholder: common('model'),
                        options: modelOptions.map((model) => {
                            return {
                                ...model,
                                label:
                                    ai_agent_config?.agent_setup?.models?.find((item) => item.value === model.value)
                                        ?.label || temporallyLabelOpenAiToOracle(model.label),
                            };
                        }),
                        type: 'select',
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                ],
            },
            skill2: {
                columns: 'grid-cols-1  mb-3',
                items: [
                    {
                        name: 'new_skill.name',
                        classNameLabel,
                        label: agent('skill_name'),
                        placeholder: agent('skill_name'),
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    // {
                    //     name: 'new_skill.description',
                    //     label: common('description'),
                    //     classNameLabel,
                    //     defaultValue: '',
                    //     classNameInput: '!min-h-[50px] !max-h-[200px] transition-all duration-300 ease-in-out resize-none',
                    //     noValidateDefaultValues: true,
                    //     placeholder: agent('skill_description_placeholder'),
                    //     type: 'textarea',
                    // },
                    // {
                    //     name: 'new_skill.prompt',
                    //     classNameLabel,
                    //     defaultValue: '',
                    //     noValidateDefaultValues: true,
                    //     label: 'Prompt',
                    //     classNameInput: '!min-h-[50px] !max-h-[200px] transition-all duration-300 ease-in-out resize-none',
                    //     placeholder: agent('skill_prompt_placeholder'),
                    //     type: 'textarea',
                    // },
                    // {
                    //     name: 'new_skill.output_format',
                    //     label: agent('output_format'),
                    //     classNameLabel,
                    //     classNameInput: '!min-h-[50px] !max-h-[200px] transition-all duration-300 ease-in-out resize-none',
                    //     placeholder: agent('output_format'),
                    //     type: 'textarea',
                    // },
                ],
            },
            skill3: {
                columns: 'grid-cols-1  mb-3',
                items: [
                    {
                        name: 'new_skill.output_component',
                        classNameLabel,
                        label: agent('output_component'),
                        placeholder: agent('output_component'),
                        options: componentCmsList,
                        type: 'select',
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: false,
                        },
                    },
                    {
                        name: 'new_skill.config.need_auth',
                        label: common('auth'),
                        placeholder: common('auth'),
                        classNameLabel,
                        classNameInput: 'data-[state=checked]:!bg-purple-700 !border-purple-700',
                        classContainer: '',
                        type: 'checkbox',
                        disabled: id !== 'create' && !allowEdit,
                        defaultValue: false,
                        noValidateDefaultValues: true,
                    },
                ],
            },
            SkillSummarize: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'new_skill.config.max_token_output',
                        label: agent('max_token_output'),
                        placeholder: agent('max_token_output'),
                        type: 'number',
                        disabled: id !== 'create' && !allowEdit,
                        classNameLabel,
                    },
                    {
                        name: 'new_skill.config.context',
                        label: agent('context'),
                        placeholder: agent('context'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                    },
                ],
            },
            SkillClassification: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'new_skill.config.examples',
                        label: common('examples'),
                        placeholder: common('examples'),
                        type: 'select',
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        options: [],
                        isMulti: true,
                    },
                    {
                        name: 'new_skill.config.classification_enum',
                        label: agent('classification_enum'),
                        placeholder: agent('classification_enum'),
                        type: 'select',
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        isMulti: true,
                        options: [],
                    },
                    {
                        name: 'new_skill.config.context',
                        label: agent('context'),
                        placeholder: agent('context'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                    },
                ],
            },
            SkillRequest: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'new_skill.config.url',
                        classNameLabel,
                        label: agent('url'),
                        placeholder: agent('url'),
                        defaultValue: '',
                        disabled: id !== 'create' && !allowEdit,
                        noValidateDefaultValues: true,
                    },
                    {
                        name: 'new_skill.config.type',
                        label: agent('type'),
                        classNameLabel,
                        placeholder: agent('type'),
                        defaultValue: 'GET',
                        noValidateDefaultValues: true,
                        disabled: id !== 'create' && !allowEdit,
                        options: [
                            { value: 'GET', label: 'GET' },
                            { value: 'POST', label: 'POST' },
                        ],
                        type: 'select',
                    },
                ],
            },
            SkillSentiment: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'new_skill.config.threshold',
                        label: common('threshold'),
                        classNameLabel,
                        placeholder: common('threshold'),
                        type: 'number',
                        disabled: id !== 'create' && !allowEdit,
                        defaultValue: 0.75,
                        noValidateDefaultValues: true,
                        min: '-Infinity', // Permite números negativos
                    },
                ],
            },
            SkillContentGenerator: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'new_skill.config.allow_generate_images',
                        label: agent('allow_generate_images'),
                        placeholder: agent('allow_generate_images'),
                        classNameLabel,
                        classNameInput: 'data-[state=checked]:!bg-purple-700 !border-purple-700',
                        classContainer: 'mt-3',
                        type: 'checkbox',
                        disabled: id !== 'create' && !allowEdit,
                        defaultValue: false,
                        noValidateDefaultValues: true,
                    },
                ],
            },
            SkillImageAnalysis: {
                columns: 'grid-cols-1 mb-3',
                items: [],
            },
            SkillDocumentAnalysis: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'new_skill.config.top_words',
                        classNameLabel,
                        label: agent('top_words'),
                        placeholder: agent('top_words'),
                        type: 'number',
                        defaultValue: 10,
                        noValidateDefaultValues: true,
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                    {
                        name: 'new_skill.config.specialized_skill',
                        classNameLabel,
                        label: agent('specialized_skill'),
                        placeholder: agent('specialized_skill'),
                        type: 'select',
                        disabled: id !== 'create' && !allowEdit,
                        onChange: (value) => {
                            setValueLocal('new_skill.config.specialized_skill', [value]);
                        },
                        options: [
                            { value: 'Tutela', label: 'Tutela' },
                            { value: 'Contrato', label: 'Contrato' },
                        ],
                    },
                ],
            },
            SkillSpeechToText: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'new_skill.config.n_tries',
                        classNameLabel,
                        label: agent('n_tries'),
                        placeholder: agent('n_tries'),
                        type: 'number',
                        disabled: id !== 'create' && !allowEdit,
                        defaultValue: 10,
                        noValidateDefaultValues: true,
                    },
                ],
            },
        };
    }, [modelOptions, vectorStorageOptions]);

    const dataAnalysisFields = useMemo((): GroupTypes<keyof AiAgentV2Data> => {
        return {
            [EnumSkillRetrieverFields.DATABASE]: {
                columns: 'grid-cols-1 lg:grid-cols-2 mb-3',
                items: [
                    {
                        name: 'new_skill.config.db_host',
                        label: agent('db_host'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        placeholder: agent('db_host'),
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.DATABASE),
                    },
                    {
                        name: 'new_skill.config.db_name',
                        label: agent('db_name'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        placeholder: agent('db_name'),
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.DATABASE),
                    },
                    {
                        name: 'new_skill.config.db_password',
                        label: agent('db_password'),
                        classNameLabel,
                        disabled: id !== 'create' && !allowEdit,
                        placeholder: agent('db_password'),
                        type: 'password',
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.DATABASE),
                    },
                    {
                        name: '',
                        label: '',
                        classNameContentInput: 'hidden',
                        placeholder: '',
                        type: '',
                    },
                ],
                label: SkillType.SkillDataAnalysis,
                description: agent('description_item_retriever_database'),
                icon: Database,
            },
            [EnumSkillRetrieverFields.REPORT]: {
                columns: 'grid-cols-1 lg:grid-cols-2 mb-3',
                items: [
                    {
                        name: 'new_skill.config.context',
                        label: agent('context'),
                        classNameLabel,
                        placeholder: agent('context'),
                        disabled: id !== 'create' && !allowEdit,
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.REPORT),
                    },
                    {
                        name: 'new_skill.config.report_key',
                        label: agent('report_key'),
                        classNameLabel,
                        placeholder: agent('report_key'),
                        onChange: () => unregisterUnusedRetrieverFields(EnumSkillRetrieverFields.REPORT),
                        type: 'select',
                        disabled: id !== 'create' && !allowEdit,
                        options: reportsOptions.map((item) => ({ label: item.name, value: item._KEY })),
                    },
                ],
                label: SkillType.SkillDataAnalysis,
                description: agent('description_item_retriever_report'),
                icon: FileSpreadsheet,
            },
            [EnumSkillRetrieverFields.EXCEL]: {
                columns: 'grid-cols-1 lg:grid-cols-1 mb-3',
                items: [],
                label: SkillType.SkillDataAnalysis,
                description: agent('description_item_retriever_excel'),
                icon: FileSpreadsheet,
            },
        };
    }, []);

    const onError: SubmitErrorHandler<SkillForm> = (error) => {
        if (Object.keys(error).length > 0) {
            const requiredFields = getRequiredFields(error);
            if (requiredFields.length > 0) {
                toast(
                    <div className="flex flex-col">
                        <b>{customMessage('should_complete_following_fields')}:</b>
                        <ul className="list-disc px-3">
                            {requiredFields.map((field) => {
                                return <li key={field}>{agent(field) || common(field) || field}</li>;
                            })}
                        </ul>
                    </div>,
                    { richColors: true }
                );
            }
        }
    };

    const determineSelectedSource = (skill: AgentSkills) => {
        const sourcePriority = {
            [SkillType.SkillStructuredRetriever]: [
                { field: 'db_host', source: EnumSkillRetrieverFields.DATABASE },
                { field: 'model_name', source: EnumSkillRetrieverFields.SBX },
                { field: 'file', source: EnumSkillRetrieverFields.EXCEL },
                { field: 'context', source: EnumSkillRetrieverFields.REPORT },
            ],
            [SkillType.SkillDataAnalysis]: [
                { field: 'db_host', source: EnumSkillRetrieverFields.DATABASE },
                { field: 'context', source: EnumSkillRetrieverFields.REPORT },
                { field: 'file', source: EnumSkillRetrieverFields.EXCEL },
            ],
        };

        const priorities = sourcePriority[skill.skill_type as keyof typeof sourcePriority];
        if (priorities) {
            for (const { field, source } of priorities) {
                if (skill?.config[field as keyof typeof skill.config] !== null) {
                    setSelectedSource(source);
                    break;
                }
            }
        }
    };

    const updateSkill = async (skill: AgentSkills, skill_index: number) => {
        determineSelectedSource(skill);

        const folderKeys = extractFolderKeys(skill);

        if (folderKeys.length > 0) {
            const addSkillPromise = () => addFilesByFolder(skill);

            toast.promise(addSkillPromise(), {
                loading: `${common('loading')} ${common('files')}`,
                success: async (nSkill) => {
                    // Una vez que la promesa de addFilesByFolder se resuelva, llamamos a reset
                    nSkill = { ...nSkill };
                    setOldValues({
                        description: nSkill.description,
                        description_en: nSkill.description_en,
                        prompt: nSkill.prompt,
                        prompt_en: nSkill.prompt_en,
                        output_format: nSkill.output_format,
                        output_format_en: nSkill.output_format_en,
                    });

                    reset({
                        skills: getValuesContext('new_skills'),
                        new_skill: nSkill,
                        skill_index,
                    });
                    return common('load_finished');
                },
                error: 'Error while adding the skill',
            });
        } else {
            setOldValues({
                description: skill.description,
                description_en: skill.description_en,
                prompt: skill.prompt,
                prompt_en: skill.prompt_en,
                output_format: skill.output_format,
                output_format_en: skill.output_format_en,
            });

            reset({
                skills: getValuesContext('new_skills'),
                new_skill: skill,
                skill_index,
            });
        }
    };

    const openSkillsModal = () => {
        dispatchApp(
            appActions.openModal({
                type: ModalType.DYNAMIC,
                data: {
                    title: agent('skills'),
                    className: 'h-[90vh] max-w-[90vw] overflow-auto bg-gradient-to-br from-pink-100 to-indigo-100 pb-5',
                    onClose: () => dispatchApp(appActions.closeModal(ModalType.DYNAMIC)),
                    children: () => {
                        return (
                            <TableComponentV2
                                search
                                items={skillOptions}
                                pagination
                                header={[
                                    {
                                        type: KeyHeader.KEY,
                                        label: common('label'),
                                        key: 'label',
                                    },
                                    {
                                        type: KeyHeader.KEY,
                                        label: common('description'),
                                        key: 'description',
                                    },
                                    {
                                        type: KeyHeader.KEY,
                                        label: common('example'),
                                        key: 'example',
                                    },
                                ]}
                                actions={[
                                    {
                                        icon: <CheckIcon className="text-purple-600 hover:text-purple-900" />,
                                        label: common('click_to_select'),
                                        onClick: (item) => {
                                            if (
                                                item.name.includes('Retriever') &&
                                                item.label.toLowerCase().includes('text')
                                            ) {
                                                onChangeSkillType(SkillType.SkillUnstructuredRetriever);
                                            } else if (
                                                item.name.includes('Retriever') &&
                                                item.label.toLowerCase().includes('dat')
                                            ) {
                                                onChangeSkillType(SkillType.SkillStructuredRetriever);
                                            } else {
                                                onChangeSkillType(item.name as SkillType);
                                            }
                                            dispatchApp(appActions.closeModal(ModalType.DYNAMIC));
                                        },
                                    },
                                ]}
                            />
                        );
                    },
                },
            })
        );
    };

    const updateLearnSkillService: SubmitHandler<SkillForm> = async (data) => {
        const skill = { ...data.new_skill };

        if (!skill) return;
        const nSkill = new AgentSkillClass(skill, ai_agent_config?.folders?.knowledge || '', {
            retriever: skill.skill_type === SkillType.SkillStructuredRetriever,
            oldValues,
        });

        if (skill) {
            const validation = nSkill.isValidSkill();
            if (validation) {
                toast.error(agent(validation));
                return;
            }
        }

        if (nSkill && id) {
            await nSkill.validateAndTranslateSkill();
            if (nSkill.skill_id) {
                nSkill.skill_id = parseInt(nSkill.skill_id.toString());
            }

            const response = await nSkill.learningManual(parseInt(id), user_id);
            if (!response?.success) {
                toast.error(`${agent('agent_created_skill_error')} ${nSkill.getSkill().name}`);
            }
        }
    };

    return loading ? (
        <AnimatedLoadingSkill message={`${agent('processing')} ${agent('skill')}`} />
    ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-2">
            {!isDesktopOrLaptop ? (
                <div className="flex justify-end">
                    <SheetComponent
                        buttonLabel={`${common('show')} ${agent('skills')}`}
                        buttonIcon={<Eye />}
                        title={''}
                        buttonClassName={`bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold py-2 px-6 rounded-full transition-all duration-300 transform hover:scale-105`}
                        contentClassName="pt-12 w-full !max-w-[100vw] xl:w-[55vw] xl:max-w-[55vw] xxl:max-w-[45vw] xxl:w-[45vw] lg:w-[55vw] lg:max-w-[55vw] "
                    >
                        <AgentSkillListComponent removeSkill={removeSkill} updateSkill={updateSkill} fields={fields} />
                    </SheetComponent>
                </div>
            ) : null}
            <Card className=" bg-gradient-to-br from-pink-100 to-purple-100 shadow-xl">
                <CardHeader className="flex !flex-row items-center justify-between flex-nowrap gap-[2rem]">
                    <CardTitle className="text-2xl font-bold text-purple-800">
                        {
                            // agent(watch("new_skill.skill_id") ? 'update_skill' : 'new_skill')
                            watch('new_skill.skill_id') || typeof watch('skill_index') === 'number'
                                ? watch('new_skill.name')
                                : `${common('add')} ${agent('skill')}`
                        }
                    </CardTitle>

                    <div className="flex justify-end items-center">
                        {((id === 'create' && allowCreate) || (id !== 'create' && allowEdit)) && (
                            <Button
                                size={'sm'}
                                onClick={() => {
                                    if (id === 'create') {
                                        handleSubmit(addSkill, onError)();
                                    } else {
                                        dispatchApp(
                                            appActions.openModal({
                                                type: ModalType.CONFIRMATION,
                                                data: {
                                                    titleClassName: 'text-2xl font-bold text-purple-800 text-center',
                                                    title: watch('new_skill.skill_id')
                                                        ? agent('update_skill')
                                                        : `${common('add')} ${agent('skill')}?`,
                                                    description: '',
                                                    className:
                                                        '!bg-gradient-to-br !from-purple-50 !to-pink-50 !rounded-3xl !shadow-2xl w-full max-w-md overflow-hidden',
                                                    cancelButtonClassName:
                                                        'border-pink-500 btn text-pink-600 hover:bg-pink-50 w-[200px]',
                                                    confirmButtonClassName:
                                                        'bg-gradient-to-r btn from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white w-[200px]',
                                                    state: DataStates.AGENT,
                                                    async onConfirm() {
                                                        dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
                                                        handleSubmit(addSkill, onError)();
                                                    },
                                                    onClose() {
                                                        dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
                                                    },
                                                },
                                            })
                                        );
                                    }
                                }}
                                className="w-full primary-gradient-button"
                            >
                                <PlusCircle className="mr-2 h-4 w-4" />
                                {watch('new_skill.skill_id') || typeof watch('skill_index') === 'number'
                                    ? agent('update_skill')
                                    : `${common('add')} ${agent('skill')}`}
                            </Button>
                        )}

                        {watch('new_skill.skill_id') && (
                            <Button
                                size="sm"
                                onClick={() => {
                                    toast.promise(handleSubmit(updateLearnSkillService, onError)(), {
                                        loading: `${common('loading')}`,
                                        success: async () => {
                                            return common('update_finished');
                                        },
                                        error: 'Error while adding the skill',
                                    });
                                }}
                                className="w-full primary-gradient-button ml-4"
                            >
                                {agent('update_learn')}
                            </Button>
                        )}

                        {watch('new_skill.skill_id') && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    reset({
                                        skills: getValuesContext('new_skills'),
                                        new_skill: undefined,
                                    });
                                }}
                                className="text-gray-400 hover:text-gray-600 hover:bg-transparent"
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-col gap-2">
                        <div className={`grid ${skillsField['skill'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}>
                            <div className="relative flex flex-col">
                                <label className={`block text-sm leading-6 ${classNameLabel}`}>
                                    {common('type')}
                                    {<sup className="text-[#dc3545] ml-1">*</sup>}
                                </label>
                                <button disabled={id !== 'create' && !allowEdit} onClick={openSkillsModal}>
                                    <input
                                        type="text"
                                        className={`w-full h-10 font-medium rounded border-gray-400 border pl-4 pr-8 py-2 text-sm bg-background outline-none ${id !== 'create' && !allowEdit ? 'cursor-not-allowed' : 'cursor-pointer focus:ring-2 focus:ring-primary focus:ring-inset hover:bg-accent hover:text-accent-foreground'}`}
                                        placeholder={`${report('select')} ${common('action')}`}
                                        readOnly
                                        value={agent((getValues('new_skill.skill_type') as string) || '')}
                                    />
                                    <Search className={`absolute top-1/2 right-2 text-gray-400 w-[20px] transition-all ${id !== 'create' && !allowEdit ? 'cursor-not-allowed' : 'cursor-pointer hover:text-gray-500 hover:scale-110'}`} />
                                </button>
                            </div>
                            <RenderFormFieldsComponent
                                unregister={unregister}
                                fields={skillsField['skill'].items}
                                control={control}
                            />
                        </div>
                        <div className={`grid ${skillsField['skill2'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}>
                            <RenderFormFieldsComponent
                                unregister={unregister}
                                fields={skillsField['skill2'].items}
                                control={control}
                            />
                        </div>

                        <div className="grid gap-3">
                            <TextAreaTranslateComponent
                                originalPlaceholder={agent('skill_description_placeholder')}
                                translatePlaceholder={agent('skill_description_placeholder')}
                                keyFormOriginal="new_skill.description"
                                keyFormTranslate="new_skill.description_en"
                                label={common('description')}
                                getValues={getValues}
                                setValue={setValueLocal}
                                watch={watch}
                                classNameTextArea={classNameTextArea}
                                disabled={id !== 'create' && !allowEdit}
                            />

                            <TextAreaTranslateComponent
                                originalPlaceholder={agent('skill_prompt_placeholder')}
                                translatePlaceholder={agent('skill_prompt_placeholder')}
                                keyFormOriginal="new_skill.prompt"
                                keyFormTranslate="new_skill.prompt_en"
                                label={'Prompt'}
                                getValues={getValues}
                                setValue={setValueLocal}
                                watch={watch}
                                classNameTextArea={classNameTextArea}
                                disabled={id !== 'create' && !allowEdit}
                            />

                            <TextAreaTranslateComponent
                                originalPlaceholder={agent('output_format')}
                                translatePlaceholder={agent('output_format')}
                                keyFormOriginal="new_skill.output_format"
                                keyFormTranslate="new_skill.output_format_en"
                                label={agent('output_format')}
                                getValues={getValues}
                                setValue={setValueLocal}
                                watch={watch}
                                classNameTextArea={classNameTextArea}
                                disabled={id !== 'create' && !allowEdit}
                            />
                        </div>

                        <div className={`grid ${skillsField['skill3'].columns ?? 'grid-cols-1 '} gap-3 mb-3`}>
                            <RenderFormFieldsComponent
                                unregister={unregister}
                                fields={skillsField['skill3'].items}
                                control={control}
                            />
                        </div>

                        {watch('new_skill.skill_type') && watch('new_skill.skill_type') === SkillType.SkillSummarize ? (
                            <SkillFileComponent
                                setFiles={(files) => {
                                    setValueLocal('new_skill.config.files', files);
                                }}
                                files={(watch('new_skill.config.files') as File[]) ?? []}
                                filesComponent={(watch('new_skill.config.filesComponent') as FileProps[]) ?? []}
                                setFilesComponent={(files) => {
                                    setValueLocal('new_skill.config.filesComponent', files);
                                }}
                                deleteFile={(file) => deleteFileFolder({ file, name: 'config' })}
                            />
                        ) : null}

                        {watch('new_skill.skill_type') &&
                        watch('new_skill.skill_type') === SkillType.SkillDocumentAnalysis ? (
                            <>
                                <SkillSummarizeConfig
                                    setValue={setValueLocal}
                                    control={control}
                                    skillsField={skillsField}
                                    skillRetrieverFields={skillRetrieverFields}
                                    watch={watch}
                                    register={register}
                                    deleteFileFolder={deleteFileFolder}
                                />

                                <Accordion type="single" collapsible className="w-full">
                                    <AccordionItem value="item-2">
                                        <AccordionTrigger className="accordion-trigger-label">
                                            {agent('sentiment_config')}
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <RenderFormFieldsComponent
                                                fields={skillsField[SkillType.SkillSentiment].items.map((item) => {
                                                    const name = item.name.split('.').at(-1);
                                                    return {
                                                        ...item,
                                                        name: `new_skill.config.sentiment_config.${name}`,
                                                    };
                                                })}
                                                control={control}
                                            />
                                        </AccordionContent>
                                    </AccordionItem>

                                    <AccordionItem value="item-3">
                                        <AccordionTrigger className="accordion-trigger-label">
                                            {agent('classification_config')}
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            {/*<SkillActionRetrieverComponent*/}
                                            {/*    control={control}*/}
                                            {/*    parentConfig={'classification_config.action_retriever_config'}*/}
                                            {/*    customRetrieverFields={name => <SkillCustomRetrieverFields*/}
                                            {/*        name={'config.classification_config.action_retriever_config'}*/}
                                            {/*        setFilesComponent={files => {*/}
                                            {/*            setValueLocal(`new_skill.config.classification_config.action_retriever_config.filesComponent`, files);*/}
                                            {/*        }}*/}
                                            {/*        setFiles={files => {*/}
                                            {/*            setValueLocal(`new_skill.config.classification_config.action_retriever_config.files` as any, files);*/}
                                            {/*        }}*/}
                                            {/*        files={watch(`new_skill.config.classification_config.action_retriever_config.files` as any) ?? []}*/}
                                            {/*        filesComponent={(watch(`new_skill.config.classification_config.action_retriever_config.filesComponent` as any) ?? []) as FileProps[]}*/}
                                            {/*        control={control}*/}
                                            {/*        watch={watch}*/}
                                            {/*        deleteFile={file => deleteFileFolder({ file, name })}*/}
                                            {/*    />}*/}
                                            {/*    register={register}*/}
                                            {/*    skillRetrieverFields={skillRetrieverFields}*/}
                                            {/*/>*/}

                                            <RenderFormFieldsComponent
                                                fields={skillsField[SkillType.SkillClassification].items.map((item) => {
                                                    const name = item.name.split('.').at(-1);
                                                    return {
                                                        ...item,
                                                        name: `new_skill.config.classification_config.${name}`,
                                                    };
                                                })}
                                                control={control}
                                            />
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </>
                        ) : null}

                        {watch('new_skill.skill_type') &&
                        watch('new_skill.skill_type') === SkillType.SkillUnstructuredRetriever ? (
                            <SkillRetrieverComponent
                                skillRetrieverFields={skillRetrieverFields}
                                register={register}
                                name={'config'}
                                structured={watch('new_skill.skill_type') === SkillType.SkillStructuredRetriever}
                                watch={watch}
                                getValues={getValues}
                                customRetrieverFields={(name: string, group?: string) => (
                                    <SkillCustomRetrieverFields
                                        key={'skill_retriever_config'}
                                        register={register}
                                        name={name}
                                        group={group}
                                        getValues={getValues}
                                        setValues={setValueLocal}
                                        setFilesComponent={(files) => {
                                            setValueLocal(`new_skill.config.filesComponent` as any, files);
                                        }}
                                        setFiles={(files) => {
                                            setValueLocal(`new_skill.config.files` as any, files);
                                        }}
                                        files={watch(`new_skill.config.files` as any) ?? []}
                                        filesComponent={
                                            (watch(`new_skill.config.filesComponent` as any) ?? []) as FileProps[]
                                        }
                                        control={control}
                                        watch={watch}
                                        deleteFile={(file) => deleteFileFolder({ file, name })}
                                    />
                                )}
                                control={control}
                            />
                        ) : skillsField[watch('new_skill.skill_type')] ? (
                            <div
                                className={`grid ${skillsField[watch('new_skill.skill_type')].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                            >
                                <RenderFormFieldsComponent
                                    unregister={unregister}
                                    fields={skillsField[watch('new_skill.skill_type')].items}
                                    control={control}
                                />
                            </div>
                        ) : null}

                        {watch('new_skill.skill_type') &&
                            watch('new_skill.skill_type') === SkillType.SkillStructuredRetriever && (
                                <SkillRetrieverComponentDataSourceVersion
                                    skillRetrieverFields={skillRetrieverFields}
                                    register={register}
                                    name={'config'}
                                    watch={watch}
                                    getValues={getValues}
                                    selectedSource={selectedSource}
                                    setSelectedSource={setSelectedSource}
                                    control={control}
                                    unregisterUnusedRetrieverFields={unregisterUnusedRetrieverFields}
                                    currentColumnsToReturn={currentColumnsToReturn}
                                    setCurrentColumnsToReturn={setCurrentColumnsToReturn}
                                    setValues={setValueLocal}
                                    customRetrieverFields={(name: string, group?: string) => (
                                        <SkillCustomRetrieverFields
                                            key={'skill_retriever_config'}
                                            register={register}
                                            name={name}
                                            group={group}
                                            getValues={getValues}
                                            setValues={setValueLocal}
                                            setFilesComponent={(files) => {
                                                setValueLocal(`new_skill.config.filesComponent` as any, files);
                                            }}
                                            setFiles={(files) => {
                                                setValueLocal(`new_skill.config.files` as any, files);
                                            }}
                                            files={watch(`new_skill.config.files` as any) ?? []}
                                            filesComponent={
                                                (watch(`new_skill.config.filesComponent` as any) ?? []) as FileProps[]
                                            }
                                            control={control}
                                            watch={watch}
                                            deleteFile={(file) => deleteFileFolder({ file, name })}
                                            unregisterUnusedRetrieverFields={unregisterUnusedRetrieverFields}
                                        />
                                    )}
                                />
                            )}

                        {watch('new_skill.skill_type') &&
                        watch('new_skill.skill_type') === SkillType.SkillDataAnalysis ? (
                            <SkillRetrieverComponentDataSourceVersion
                                skillRetrieverFields={dataAnalysisFields}
                                register={register}
                                name={'config'}
                                watch={watch}
                                getValues={getValues}
                                selectedSource={selectedSource}
                                setSelectedSource={setSelectedSource}
                                control={control}
                                unregisterUnusedRetrieverFields={unregisterUnusedRetrieverFields}
                                currentColumnsToReturn={currentColumnsToReturn}
                                setCurrentColumnsToReturn={setCurrentColumnsToReturn}
                                setValues={setValueLocal}
                                dataScience
                                customRetrieverFields={(name: string, group?: string) => (
                                    <SkillCustomRetrieverFields
                                        key={'skill_retriever_config'}
                                        register={register}
                                        name={name}
                                        group={group}
                                        getValues={getValues}
                                        setValues={setValueLocal}
                                        setFilesComponent={(files) => {
                                            setValueLocal(`new_skill.config.filesComponent` as any, files);
                                        }}
                                        setFiles={(files) => {
                                            setValueLocal(`new_skill.config.files` as any, files);
                                        }}
                                        files={watch(`new_skill.config.files` as any) ?? []}
                                        filesComponent={
                                            (watch(`new_skill.config.filesComponent` as any) ?? []) as FileProps[]
                                        }
                                        control={control}
                                        watch={watch}
                                        deleteFile={(file) => deleteFileFolder({ file, name })}
                                        unregisterUnusedRetrieverFields={unregisterUnusedRetrieverFields}
                                        dataScience
                                    />
                                )}
                            />
                        ) : null}

                        {watch('new_skill.skill_type') && watch('new_skill.skill_type') === SkillType.SkillRequest ? (
                            <SkillRequestComponent control={control} register={register} />
                        ) : null}

                        {[
                            SkillType.SkillDataAnalysis,
                            SkillType.SkillClassification,
                            SkillType.SkillSummarize,
                        ].includes(watch('new_skill.skill_type') as SkillType)
                            ? null
                            : // <SkillActionRetrieverComponent
                              //     control={control}
                              //     customRetrieverFields={name => <SkillCustomRetrieverFields
                              //         name={name}
                              //         setFilesComponent={files => {
                              //             setValueLocal(`new_skill.${name}.filesComponent` as any, files);
                              //         }}
                              //         setFiles={files => {
                              //             setValueLocal(`new_skill.${name}.files` as any, files);
                              //         }}
                              //         files={watch(`new_skill.${name}.files` as any) ?? []}
                              //         filesComponent={(watch(`new_skill.${name}.filesComponent` as any) ?? []) as FileProps[]}
                              //         control={control}
                              //         watch={watch}
                              //         deleteFile={file => deleteFileFolder({ file, name })}
                              //     />}
                              //     register={register}
                              //     skillRetrieverFields={skillRetrieverFields}
                              // />

                              null}
                    </div>
                </CardContent>
            </Card>

            {isDesktopOrLaptop ? (
                <AgentSkillListComponent updateSkill={updateSkill} removeSkill={removeSkill} fields={fields} />
            ) : null}
        </div>
    );
};

export default AgentSkillsComponent;
